/* eslint-disable arrow-body-style */
// 弹窗内部状态管理：初始化/修复筛选器与指标选中值，级联过滤可选项，保证至少保留一个选中
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  IFormattedFilter,
  IMetricOption,
  TSelectedFilters,
  TSelectedMetrics,
} from './_type';
import { getDissionLable } from '../AIContent/_utils';

interface IUseDownloadSettingsParams {
  visible: boolean;
  currentFilterVal: { [key: string]: string; };
  currentMetric: string;
  showFilters: IFormattedFilter[];
  showMetrics: IMetricOption[];
  // 从 useDownloadData 传入的有效选中值
  effectiveSelected?: {
    filters: TSelectedFilters;
    metrics: TSelectedMetrics;
  };
  // 全量筛选器和实时计算函数
  allFilters?: any[];
  getFilteredOptions?: (filter: any, selected: TSelectedFilters) => any[];
  filterMetricsByCascade?: (selectedFilters: TSelectedFilters) => Set<string>;
  getDimensionName?: (key: string) => string;
  metricsInfo?: any[];
}

/**
 * 下载设置弹窗的 Hook
 * 只负责管理选中状态，数据准备已在 useDownloadData 中完成
 */
export const useDownloadSettings = (params: IUseDownloadSettingsParams) => {
  const {
    visible,
    currentFilterVal,
    currentMetric,
    showFilters: initialShowFilters,
    showMetrics: initialShowMetrics,
    effectiveSelected,
    allFilters = [],
    getFilteredOptions,
    filterMetricsByCascade,
    getDimensionName,
    metricsInfo = [],
  } = params;

  // 筛选器选中值（多选）
  const [selectedFilters, setSelectedFilters] = useState<TSelectedFilters>({});
  // 指标选中值（多选）
  const [selectedMetrics, setSelectedMetrics] = useState<TSelectedMetrics>([]);

  // 初始化选中值：使用 effectiveSelected 或当前值
  useEffect(() => {
    if (visible) {
      const initialFilters: TSelectedFilters = {};

      initialShowFilters.forEach((filter) => {
        // 优先使用 effectiveSelected 中的值
        if (effectiveSelected?.filters?.[filter.data_key]) {
          initialFilters[filter.data_key] = effectiveSelected.filters[filter.data_key];
        } else {
          // 否则使用当前值或自动选中单个选项
          const currentValue = currentFilterVal?.[filter.data_key];
          const options = filter.options || [];
          const hasSingleOption = options.length === 1;

          if (hasSingleOption && options[0]?.value) {
            // 唯一可选值：自动选中
            initialFilters[filter.data_key] = [options[0].value];
          } else if (currentValue) {
            initialFilters[filter.data_key] = [currentValue];
          } else {
            initialFilters[filter.data_key] = [];
          }
        }
      });

      // 指标初始化
      let initialMetrics: string[] = [];
      if (effectiveSelected?.metrics && effectiveSelected.metrics.length > 0) {
        initialMetrics = effectiveSelected.metrics;
      } else if (initialShowMetrics.length === 1 && initialShowMetrics[0]?.value) {
        // 只有一个指标时自动选中
        initialMetrics = [initialShowMetrics[0].value];
      } else if (currentMetric) {
        initialMetrics = [currentMetric];
      }

      setSelectedFilters(initialFilters);
      setSelectedMetrics(initialMetrics);
    }
  }, [visible, currentFilterVal, currentMetric, initialShowFilters, initialShowMetrics, effectiveSelected]);

  // 根据 selectedFilters 实时计算筛选器选项
  const filterOptionsList = useMemo<IFormattedFilter[]>(() => {
    if (!getFilteredOptions || !getDimensionName || allFilters.length === 0) {
      return initialShowFilters;
    }

    return allFilters.map((filter) => {
      const key = filter?.data_key || '';
      const rawValueList = getFilteredOptions(filter, selectedFilters);
      const filterName = filter?.name || getDimensionName(key) || key;

      const options = rawValueList.map((item) => {
        if (typeof item === 'object' && item !== null) {
          const displayLabel = getDissionLable(key, item.value, item?.name);
          return {
            text: displayLabel || item?.value,
            value: item?.value,
            icon: item?.icon || '',
            icon_active: item?.icon_active || '',
          };
        }
        const displayLabel = getDissionLable(key, item);
        return {
          text: displayLabel || item,
          value: item,
          icon: '',
          icon_active: '',
        };
      });

      // 合并重复的选项（按 value 去重，区分大小写）
      const valueMap = new Map<string, {
        text: string;
        value: string;
        icon: string;
        icon_active: string;
      }>();

      options.forEach((option) => {
        // 使用 value 作为 key（区分大小写）
        const valueKey = option.value || '';
        const existing = valueMap.get(valueKey);
        
        if (existing) {
          // 如果已存在相同 value（完全匹配），保留第一个非空的 text、icon、icon_active
          if (!existing.text && option.text) {
            existing.text = option.text;
          }
          if (!existing.icon && option.icon) {
            existing.icon = option.icon;
          }
          if (!existing.icon_active && option.icon_active) {
            existing.icon_active = option.icon_active;
          }
        } else {
          // 保留第一个出现的 value
          valueMap.set(valueKey, {
            text: option.text,
            value: option.value,
            icon: option.icon || '',
            icon_active: option.icon_active || '',
          });
        }
      });

      const mergedOptions = Array.from(valueMap.values());

      return {
        data_key: key,
        name: filterName,
        options: mergedOptions,
      };
    });
  }, [allFilters, selectedFilters, getFilteredOptions, getDimensionName, initialShowFilters]);

  // 自动修复无效的筛选器选中值（当选项变化时）
  useEffect(() => {
    if (!getFilteredOptions || allFilters.length === 0 || !visible) {
      return;
    }

    const updatedFilters = { ...selectedFilters };
    let needUpdate = false;

    allFilters.forEach((filter) => {
      const key = filter?.data_key || '';
      const currentSelected = selectedFilters[key] || [];
      if (currentSelected.length === 0) return; // 如果没有选中值，跳过

      const rawValueList = getFilteredOptions(filter, selectedFilters);
      const availableValues = new Set(
        rawValueList.map((item) => (typeof item === 'string' ? item : item.value))
      );

      // 过滤掉无效的选中值
      const validSelected = currentSelected.filter((val) => availableValues.has(val));

      // 如果选中值全部无效，自动选中第一个可用值
      if (validSelected.length === 0 && availableValues.size > 0) {
        const firstValue = Array.from(availableValues)[0];
        updatedFilters[key] = [firstValue];
        needUpdate = true;
      } else if (validSelected.length !== currentSelected.length) {
        // 部分无效，只保留有效的（但至少保留一个）
        updatedFilters[key] = validSelected.length > 0 ? validSelected : [Array.from(availableValues)[0]];
        needUpdate = true;
      }
    });

    // 如果有更新，同步到 state
    if (needUpdate) {
      setSelectedFilters(updatedFilters);
    }
  }, [filterOptionsList, selectedFilters, getFilteredOptions, allFilters, visible]);

  // 根据 selectedFilters 实时计算指标选项
  const metricOptions = useMemo<IMetricOption[]>(() => {
    if (!filterMetricsByCascade || metricsInfo.length === 0) {
      return initialShowMetrics;
    }

    const availableMetricKeys = filterMetricsByCascade(selectedFilters);
    // availableMetricKeys.size === 0 表示不过滤，显示全部指标
    const enableFilter = availableMetricKeys.size > 0;
    const list = enableFilter
      ? metricsInfo.filter((m) => availableMetricKeys.has(m.data_key || ''))
      : metricsInfo;
    
    return list.map((metric) => ({
      text: metric.name || metric.data_key || '',
      value: metric.data_key || '',
    }));
  }, [selectedFilters, filterMetricsByCascade, metricsInfo, initialShowMetrics]);

  // 自动修复无效的指标选中值（当选项变化时）
  useEffect(() => {
    if (!filterMetricsByCascade || metricsInfo.length === 0 || !visible) {
      return;
    }

    const availableMetricKeys = filterMetricsByCascade(selectedFilters);
    const enableFilter = availableMetricKeys.size > 0;
    const metricValue = metricOptions?.[0]?.value || '';
    if (enableFilter && selectedMetrics.length > 0) {
      const validMetrics = selectedMetrics.filter((m) => availableMetricKeys.has(m));
      if (validMetrics.length === 0 && metricOptions.length > 0) {
        // 如果选中值全部无效，自动选中第一个可用值
        metricValue && setSelectedMetrics([metricValue]);
      } else if (validMetrics.length !== selectedMetrics.length) {
        // 部分无效，只保留有效的（但至少保留一个）
        const finalMetrics = validMetrics.length > 0 ? validMetrics : [metricValue];
        metricValue && setSelectedMetrics(finalMetrics);
      }
    } else if (!enableFilter && selectedMetrics.length === 0 && metricOptions.length > 0) {
      // 如果不过滤且没有选中值，自动选中第一个
      metricValue && setSelectedMetrics([metricValue]);
    }
  }, [metricOptions, selectedFilters, selectedMetrics, filterMetricsByCascade, metricsInfo, visible]);

  // 切换指标选中状态（至少保留一个）
  const toggleMetric = useCallback((value: string): void => {
    setSelectedMetrics((prev) => {
      if (prev.includes(value)) {
        // 如果要取消选中，检查是否至少保留一个
        if (prev.length <= 1) {
          // 至少保留一个，不允许取消
          return prev;
        }
        return prev.filter((item) => item !== value);
      }
      return [...prev, value];
    });
  }, []);

  // 切换筛选器选中状态（至少保留一个，并处理子级自动选中）
  const toggleFilter = useCallback((dataKey: string, value: string): void => {
    setSelectedFilters((prev) => {
      const current = prev[dataKey] || [];
      let nextSelected = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];

      // 至少保留一个选中值
      if (nextSelected.length === 0) {
        // 如果取消后为空，至少保留一个（不允许全部取消）
        nextSelected = current.length > 0 ? [current[0]] : [value];
      }

      const next = {
        ...prev,
        [dataKey]: nextSelected,
      };

      // 级联：当父级变化时，检查并修复子级选中值
      if (getFilteredOptions && allFilters.length > 0) {
        // 找到所有子级筛选器
        allFilters.forEach((filter) => {
          const filterKey = filter.data_key;
          if (filterKey === dataKey) return; // 跳过当前筛选器

          // 检查是否是子级（通过 display_info 判断）
          const displayInfo = (filter as any)?.display_info as string[] | undefined;
          if (!displayInfo || displayInfo.length === 0) return;

          const currentIndex = displayInfo.findIndex((key) => key === dataKey);
          const childIndex = displayInfo.findIndex((key) => key === filterKey);
          
          // 如果是子级
          if (currentIndex >= 0 && childIndex > currentIndex) {
            const childSelected = next[filterKey] || [];
            const availableOptions = getFilteredOptions(filter, next);
            const availableValues = new Set(
              availableOptions.map((item) => (typeof item === 'string' ? item : item.value))
            );

            // 过滤掉无效的选中值
            const validSelected = childSelected.filter((val) => availableValues.has(val));

            // 如果子级选中值全部无效，自动选中第一个可用值
            if (validSelected.length === 0 && availableValues.size > 0) {
              const firstValue = Array.from(availableValues)[0];
              next[filterKey] = [firstValue];
            } else if (validSelected.length !== childSelected.length) {
              // 部分无效，只保留有效的
              next[filterKey] = validSelected;
            }
          }
        });
      }

      return next;
    });
  }, [getFilteredOptions, allFilters]);

  return {
    selectedFilters,
    selectedMetrics,
    filterOptionsList,
    metricOptions,
    toggleMetric,
    toggleFilter,
  };
};
